import { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router';

import { useEntitlementsData } from '../api/hooks/UseEntitlementsData';
import type { Entitlements } from '../types';
import { setUserContext } from '../utils/newrelic';

export interface EntitlementsContextType {
  entitlements: Entitlements | undefined;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
  hasEntitlements: boolean;
}

export const EntitlementsContext =
  createContext<EntitlementsContextType | null>(null);

export const EntitlementsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { pathname } = useLocation();

  // Only call entitlements API if on a valid path
  const shouldFetchEntitlements =
    pathname === '/' || pathname.startsWith('/details/');

  const {
    data: entitlements,
    error,
    isError,
    isLoading,
  } = useEntitlementsData({ enabled: shouldFetchEntitlements });

  useEffect(() => {
    if (entitlements?.userId) {
      setUserContext(entitlements.userId);
    }
  }, [entitlements?.userId]);

  return (
    <EntitlementsContext.Provider
      value={{
        entitlements,
        error: error as Error | null,
        isError,
        isLoading,
        hasEntitlements: shouldFetchEntitlements && !isError && !isLoading,
      }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);

  if (!context) {
    throw new Error(
      'useEntitlements must be used within an EntitlementsProvider',
    );
  }
  return context;
};
